import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, CircleNotch, ShieldCheck } from '@phosphor-icons/react';

import { verifyMfaChallenge } from '@/lib/mfaApi';
import { normaliseApiError } from '@/lib/api';
import type { LoginSession } from '@/features/auth/types';

/**
 * The second step of signing in, for an account with two-factor authentication on.
 *
 * ── WHY THIS IS A STEP AND NOT A PAGE ─────────────────────────────────────────────────────────
 * The challenge token lives for five minutes and is a live credential for exactly one call. It
 * is held in React state by the login page and passed down here; it is never put in the URL
 * (where it would land in history and in any proxy's access log), never in localStorage, and
 * never in the auth store. Closing the tab ends it, which is the correct lifetime.
 *
 * ── THE FAILURE THIS SCREEN EXISTS TO PREVENT ─────────────────────────────────────────────────
 * A person who reaches here has already given the right password. If this screen cannot finish
 * the job they are locked out of their own practice. So every failure has a way forward: a wrong
 * code says so and lets them retry, an expired challenge sends them back to the password step
 * with an explanation rather than a silent nothing, and a lost authenticator has the recovery
 * code route in front of them rather than buried in a help page.
 */

type Mode = 'totp' | 'recovery';

export function MfaChallengeStep({
  challengeToken,
  email,
  onAuthenticated,
  onRestart,
}: {
  challengeToken: string;
  /** Shown so the person can see WHICH account they are completing sign-in for. */
  email: string;
  onAuthenticated: (session: LoginSession) => void;
  /** The challenge is dead (expired, or abandoned) — go back to email + password. */
  onRestart: (reason?: string) => void;
}) {
  const [mode, setMode] = useState<Mode>('totp');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus lands on the field the moment the step appears, and again whenever the person switches
  // between authenticator and recovery code — otherwise switching mode leaves focus on the link
  // they just clicked and a keyboard user has to hunt for the new field.
  useEffect(() => {
    inputRef.current?.focus();
  }, [mode]);

  const verify = useMutation({
    mutationFn: () =>
      verifyMfaChallenge({ challengeToken, code, useRecoveryCode: mode === 'recovery' }),
    onSuccess: (session) => onAuthenticated(session),
    onError: (err) => {
      const { status, message } = normaliseApiError(err);
      // 401 on this endpoint means the CHALLENGE is finished, not that the code was mistyped —
      // a bad code is a 401 from InvalidCredentialsError too, so the two are told apart by the
      // server's own message. Anything naming the challenge sends the person back to the start,
      // because retyping a code against a dead token can only fail forever.
      if (status === 401 && message && /challenge|expired|log in again/i.test(message)) {
        onRestart(message);
        return;
      }
      setError(message ?? 'That code was not accepted. Please try again.');
      setCode('');
      inputRef.current?.focus();
    },
  });

  const isTotp = mode === 'totp';
  // 6 digits for an authenticator; a recovery code is 16 hex characters shown as XXXX-XXXX-XXXX-XXXX,
  // so anything shorter than that cannot be one. Both are only a guard against an empty submit —
  // the server is what decides whether a code is valid.
  const ready = isTotp ? /^\d{6}$/.test(code) : code.replace(/[\s-]/g, '').length >= 16;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready || verify.isPending) return;
    setError(null);
    verify.mutate();
  }

  function switchMode(next: Mode) {
    setMode(next);
    setCode('');
    setError(null);
  }

  return (
    <div className="w-full max-w-md">
      <div className="mb-6">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
          <ShieldCheck className="h-5 w-5" weight="duotone" />
        </div>
        <h2 className="mb-1 text-[24px] font-semibold tracking-tight text-foreground">
          {isTotp ? 'Enter your verification code' : 'Enter a recovery code'}
        </h2>
        <p className="text-sm text-muted-foreground">
          {isTotp
            ? 'Open your authenticator app and enter the 6-digit code for Jurivoxa.'
            : 'Enter one of the recovery codes you saved when you turned two-factor on. Each code works once.'}
        </p>
        <p className="mt-1 truncate text-sm text-muted-foreground">
          Signing in as <span className="font-medium text-foreground">{email}</span>
        </p>
      </div>

      {/*
        aria-live so a screen reader announces a rejected code. Without it the only signal that
        anything happened is the field clearing, which is silent.
      */}
      <div aria-live="polite" role="alert">
        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="mfa-code" className="mb-1.5 block text-sm font-medium text-foreground">
            {isTotp ? 'Verification code' : 'Recovery code'}
          </label>
          <input
            ref={inputRef}
            id="mfa-code"
            name="mfa-code"
            type="text"
            // `one-time-code` is what lets iOS and Android offer the code from the authenticator
            // or an SMS autofill without the person copying it by hand.
            autoComplete="one-time-code"
            inputMode={isTotp ? 'numeric' : 'text'}
            // A numeric pattern on the recovery field would reject the letters it legitimately
            // contains, so it is applied only to the 6-digit case.
            {...(isTotp ? { pattern: '[0-9]*' } : {})}
            maxLength={isTotp ? 6 : 24}
            required
            autoFocus
            spellCheck={false}
            aria-describedby="mfa-code-hint"
            value={code}
            onChange={(e) =>
              setCode(isTotp ? e.target.value.replace(/\D/g, '').slice(0, 6) : e.target.value.toUpperCase())
            }
            className={`h-12 w-full rounded-md border bg-card px-3.5 text-foreground transition placeholder:text-muted-foreground
              focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:focus:ring-brand-500/25
              ${isTotp ? 'text-center font-mono text-[22px] tracking-[0.4em]' : 'font-mono text-sm tracking-wider'}
              ${error ? 'border-red-400 dark:border-red-500/60' : 'border-border'}`}
            placeholder={isTotp ? '000000' : 'XXXX-XXXX-XXXX-XXXX'}
          />
          <p id="mfa-code-hint" className="mt-1.5 text-xs text-muted-foreground">
            {isTotp
              ? 'The code changes every 30 seconds. If it has just changed, use the new one.'
              : 'Recovery codes are the ones you were shown once, when you turned two-factor on.'}
          </p>
        </div>

        <button
          type="submit"
          disabled={!ready || verify.isPending}
          className="group inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-5 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {verify.isPending ? (
            <>
              <CircleNotch className="h-4 w-4 animate-spin" />
              Verifying…
            </>
          ) : (
            <>
              Verify and sign in
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </>
          )}
        </button>
      </form>

      <div className="mt-6 space-y-3 border-t border-border pt-5">
        {/*
          Offered on the screen itself, not hidden behind a support article. Someone who has lost
          their phone is standing in front of a court in twenty minutes; the way back in has to be
          the next thing they see.
        */}
        <button
          type="button"
          onClick={() => switchMode(isTotp ? 'recovery' : 'totp')}
          className="text-sm font-medium text-brand-600 transition hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
        >
          {isTotp ? 'Lost your device? Use a recovery code' : 'Use your authenticator app instead'}
        </button>

        <button
          type="button"
          onClick={() => onRestart()}
          className="flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Sign in as someone else
        </button>
      </div>
    </div>
  );
}
